import T from 'Components/Helpers/translate';
import AppService from 'App/legacy/AppService';
import { Feature, hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import type { ClassicTag } from 'App/resources/Model/ClassicTag';

/*
	The classic (pdf/document-template) tag catalogue, extracted from the Angular TagService
	(WEB-7834). Only getClassicTagsByEntity is consumed outside the Angular tree; everything here
	exists to serve it.

	The catalogue is built lazily rather than at module load. Every entry is a translated string, so
	it cannot be built before the language files and metadata are in place — the Angular service
	built it inside AppService.loadedPromise for the same reason. buildTags() is memoised and runs on
	first read instead, which keeps the module free of load-order assumptions.
*/

type ClassicTagMap = { [entity: string]: ClassicTag[] };
type CustomFieldKey = Parameters<typeof AppService.getCustomFields>[0];

const FILTERED_TAGS = ['Total_ListPrice_Sum', 'Total_ListPrice_Sum_NC'];

const t = T;

// The tag catalogue is plain data — strings, numbers and booleans — so a JSON round trip is a
// sufficient deep clone and avoids depending on lodash, which DocumentTemplateEditor's tests
// automock.
const clone = (tags: ClassicTag[]): ClassicTag[] => JSON.parse(JSON.stringify(tags));

const generateCustomFieldTags = (prefix: string, cfKey: CustomFieldKey): ClassicTag[] =>
	AppService.getCustomFields(cfKey).map((cf: { name: string; id: number }) => ({
		name: cf.name,
		tag: `${prefix}_Custom_${cf.id}`
	}));

let cached: ClassicTagMap | null = null;

const buildTags = (): ClassicTagMap => {
	if (cached) {
		return cached;
	}

	const tags: ClassicTagMap = {
		client: [
			{ name: t('tag.client.id'), tag: 'Cl_ID' },
			{ name: t('tag.general.companyname'), tag: 'Cl_Name' },
			{ name: t('tag.client.address.post'), tag: 'Cl_Address' },
			{ name: t('tag.client.zipcode.post'), tag: 'Cl_Zipcode' },
			{ name: t('tag.client.city.post'), tag: 'Cl_City' },
			{ name: t('tag.client.state.post'), tag: 'Cl_State' },
			{ name: t('tag.client.country.post'), tag: 'Cl_Country' },
			{ name: t('tag.client.address.visit'), tag: 'Cl_Address_Visiting' },
			{ name: t('tag.client.zipcode.visit'), tag: 'Cl_Zipcode_Visiting' },
			{ name: t('tag.client.city.visit'), tag: 'Cl_City_Visiting' },
			{ name: t('tag.client.state.visit'), tag: 'Cl_State_Visiting' },
			{ name: t('tag.client.country.visit'), tag: 'Cl_Country_Visiting' },
			{ name: t('tag.client.address.invoice'), tag: 'Cl_Address_Invoice' },
			{ name: t('tag.client.zipcode.invoice'), tag: 'Cl_Zipcode_Invoice' },
			{ name: t('tag.client.city.invoice'), tag: 'Cl_City_Invoice' },
			{ name: t('tag.client.state.invoice'), tag: 'Cl_State_Invoice' },
			{ name: t('tag.client.country.invoice'), tag: 'Cl_Country_Invoice' },
			{ name: t('tag.client.address.delivery'), tag: 'Cl_Address_Delivery' },
			{ name: t('tag.client.zipcode.delivery'), tag: 'Cl_Zipcode_Delivery' },
			{ name: t('tag.client.city.delivery'), tag: 'Cl_City_Delivery' },
			{ name: t('tag.client.state.delivery'), tag: 'Cl_State_Delivery' },
			{ name: t('tag.client.country.delivery'), tag: 'Cl_Country_Delivery' },
			{ name: t('tag.client.address.other'), tag: 'Cl_Address_Other' },
			{ name: t('tag.client.zipcode.other'), tag: 'Cl_Zipcode_Other' },
			{ name: t('tag.client.city.other'), tag: 'Cl_City_Other' },
			{ name: t('tag.client.state.other'), tag: 'Cl_State_Other' },
			{ name: t('tag.client.country.other'), tag: 'Cl_Country_Other' },
			{ name: t('tag.client.phone'), tag: 'Cl_Phone' },
			{ name: t('tag.client.fax'), tag: 'Cl_Fax' },
			{ name: t('tag.client.webpage'), tag: 'Cl_Web' },
			{ name: t('tag.client.notes'), tag: 'Cl_Notes' },
			{ name: t('tag.client.user'), tag: 'Cl_User' },
			{ name: t('tag.client.contact.id'), tag: 'Co_ID' },
			{ name: t('tag.client.contact.name'), tag: 'Co_Name' },
			{ name: t('tag.client.contact.phone'), tag: 'Co_Phone' },
			{ name: t('tag.client.contact.cellphone'), tag: 'Co_Cellphone' },
			{ name: t('tag.client.contact.email'), tag: 'Co_Email' },
			{ name: t('tag.client.contact.title'), tag: 'Co_Title' },
			{ name: t('tag.client.contact.notes'), tag: 'Co_Notes' },
			{ name: t('tag.general.today'), tag: 'Today' },
			{ name: t('tag.general.today+10'), tag: 'Today_Plus_10' },
			{ name: t('tag.general.today+15'), tag: 'Today_Plus_15' },
			{ name: t('tag.general.today+20'), tag: 'Today_Plus_20' },
			{ name: t('tag.general.today+30'), tag: 'Today_Plus_30' },
			{ name: t('tag.general.today+38'), tag: 'Today_Plus_38' },
			{ name: t('tag.general.lastday'), tag: 'LastDay' },
			{ name: t('tag.general.currentusername'), tag: 'MyName' },
			{ name: t('tag.general.currentuseremail'), tag: 'MyEmail' },
			{ name: t('tag.general.currentuserphone'), tag: 'MyPhone' },
			{ name: t('tag.general.currentusercellphone'), tag: 'MyCellPhone' },
			{ name: t('tag.general.currentusertitle'), tag: 'MyTitle' }
		],
		clientNew: [
			{
				name: t('tag.account.orgNo'),
				tag: 'Cl_Org_No'
			} /*,
		{name: t('tag.account.statusCode'), tag: 'Cl_Status'},
		{name: t('tag.account.companyFormCode'), tag: 'Cl_Company_Form'},
		{name: t('tag.account.creditRatingCode'), tag: 'Cl_Credit_Rating'},
		{name: t('tag.account.turnover'), tag: 'Cl_Turnover'},
		{name: t('tag.account.profit'), tag: 'Cl_Profit'},
		{name: t('tag.account.noEmployees'), tag: 'Cl_No_Employees'}*/
		],
		activity: [
			{ name: t('tag.activity.idDescription'), tag: 'Act_ID' },
			{ name: t('tag.activity.date'), tag: 'Act_Date' },
			{ name: t('tag.activity.time'), tag: 'Act_Time' },
			{ name: t('tag.activity.description'), tag: 'Act_Description' },
			{ name: t('tag.activity.username'), tag: 'Act_User' },
			{ name: t('tag.activity.activitytypeDescription'), tag: 'Act_Type' },
			{ name: t('tag.activity.notes'), tag: 'Act_Notes' },
			{ name: t('tag.activity.classic.contact'), tag: 'Meeting_Contact_Names' },
			{ name: t('tag.project.prefix.name'), tag: 'Project_Name' },
			{ name: t('tag.project.prefix.quota'), tag: 'Project_Quota' },
			{ name: t('tag.project.prefix.quotanc'), tag: 'Project_Quota_NC' },
			{ name: t('tag.project.prefix.startdate'), tag: 'Project_StartDate' },
			{ name: t('tag.project.prefix.enddate'), tag: 'Project_EndDate' },
			{ name: t('tag.project.prefix.notes'), tag: 'Project_Notes' }
		],
		agreement: [
			{ name: t('tag.agreement.idDescription'), tag: 'Agreement_ID' },
			{ name: t('tag.agreement.agreementdescription'), tag: 'Agreement_Description' },
			{ name: t('tag.agreement.agreementstartdate'), tag: 'Agreement_Startdate' },
			{ name: t('tag.agreement.agreementenddate'), tag: 'Agreement_Enddate' },
			{ name: t('tag.agreement.agreementinvoicestartdate'), tag: 'Agreement_Invoice_Startdate' },
			{
				name: t('tag.agreement.agreementinvoicestartdate+1y'),
				tag: 'Agreement_Invoice_Startdate_Plus1Year'
			},
			{ name: t('tag.agreement.agreementrenewaldate'), tag: 'Agreement_Renewal_Date' },
			{
				name: t('tag.agreement.agreementrenewaldate.activity'),
				tag: 'Agreement_Renewal_Activity_Created_Date'
			},
			{ name: t('tag.agreement.agreementnextorderdate'), tag: 'Agreement_Next_Order_Date' },
			{ name: t('tag.agreement.agreementintervaltype'), tag: 'Agreement_Interval_Type' },
			{ name: t('tag.agreement.agreementintervalperiod'), tag: 'Agreement_Interval_Period' },
			{ name: t('tag.agreement.notes'), tag: 'Agreement_Notes' },
			{ name: t('tag.agreement.agreementregistrationdate'), tag: 'Agreement_Registration_Datetime' },
			{ name: t('tag.agreement.agreementmodification'), tag: 'Agreement_Modification_Datetime' },
			{ name: t('tag.agreement.discount.percentage'), tag: 'Agreement_Total_Discount_Percentage' },
			{ name: t('tag.agreement.value'), tag: 'Agreement_Total_Value' },
			{ name: t('tag.agreement.value.nc'), tag: 'Agreement_Total_Value_NC' },
			{ name: t('tag.agreement.value.vat25'), tag: 'Agreement_Total_Value_VAT25' },
			{ name: t('tag.agreement.value.vat25.nc'), tag: 'Agreement_Total_Value_VAT25_NC' },
			{ name: t('tag.agreement.order.value'), tag: 'Agreement_Order_Value' },
			{ name: t('tag.agreement.order.value.nc'), tag: 'Agreement_Order_Value_NC' },
			{ name: t('tag.agreement.order.value.vat25'), tag: 'Agreement_Order_Value_VAT25' },
			{ name: t('tag.agreement.order.value.vat25.nc'), tag: 'Agreement_Order_Value_VAT25_NC' },
			{ name: t('tag.agreement.order.listprice'), tag: 'Agreement_Order_Listprice' },
			{ name: t('tag.agreement.order.listprice.nc'), tag: 'Agreement_Order_Listprice_NC' },
			{ name: t('tag.agreement.user.name'), tag: 'Agreement_User_Name' },
			{ name: t('tag.agreement.user.email'), tag: 'Agreement_User_Email' },
			{ name: t('tag.agreement.user.phone'), tag: 'Agreement_User_Phone' },
			{ name: t('tag.agreement.user.cellphone'), tag: 'Agreement_User_Cellphone' },
			{ name: t('tag.agreement.user.title'), tag: 'Agreement_User_Title' },
			{ name: t('tag.agreement.user.address'), tag: 'Agreement_User_Address' },
			{ name: t('tag.agreement.user.zipcode'), tag: 'Agreement_User_Zipcode' },
			{ name: t('tag.agreement.user.state'), tag: 'Agreement_User_State' },
			{ name: t('tag.agreement.products.tablestart'), tag: 'TableStart_Agreement_Products' },
			{ name: t('tag.agreement.products.tableend'), tag: 'TableEnd_Agreement_Products' },
			{ name: t('tag.agreement.product.name'), tag: 'Agreement_Product_Name' },
			{ name: t('tag.agreement.product.quantity'), tag: 'Agreement_Product_Quantity' },
			{
				name: t('tag.agreement.product.quantity.noDecimals'),
				tag: 'Agreement_Product_Quantity_No_Decimals'
			},
			{ name: t('tag.agreement.product.price'), tag: 'Agreement_Product_Price' },
			{ name: t('tag.agreement.product.price.item'), tag: 'Agreement_Product_Per_Item_Price' },
			{ name: t('tag.agreement.product.listprice'), tag: 'Agreement_Product_Listprice' },
			{ name: t('tag.agreement.product.listprice.item'), tag: 'Agreement_Product_Per_Item_Listprice' },
			{ name: t('tag.agreement.product.discount'), tag: 'Agreement_Product_Discount' },
			{ name: t('tag.agreement.product.discount.item'), tag: 'Agreement_Product_Per_Item_Discount' },
			{
				name: t('tag.agreement.product.discount.percentage'),
				tag: 'Agreement_Product_Percentage_Discount'
			},
			{ name: t('tag.agreement.product.value'), tag: 'Agreement_Product_Value' },
			{ name: t('tag.agreement.product.value.vat25'), tag: 'Agreement_Product_VAT25_Value' },
			{ name: t('tag.agreement.product.category.name'), tag: 'Agreement_Product_Product_Category_Name' },
			{ name: t('tag.agreement.product.total.value'), tag: 'Agreement_Product_Total_Value' },
			{ name: t('tag.agreement.product.total.value.vat25'), tag: 'Agreement_Product_Total_VAT25_Value' },
			{ name: t('tag.agreement.product.total.discount'), tag: 'Agreement_Product_Total_Discount' },
			{
				name: t('tag.agreement.product.total.discount.vat25'),
				tag: 'Agreement_Product_Total_VAT25_Discount'
			},
			{
				name: t('tag.agreement.productcategories.tablestart'),
				tag: 'TableStart_Agreement_Product_Categories'
			},
			{
				name: t('tag.agreement.productcategories.tableend'),
				tag: 'TableEnd_Agreement_Product_Categories'
			},
			{ name: t('tag.agreement.productcategorie.name'), tag: 'Agreement_Product_Category_Name' },
			{ name: t('tag.agreement.productcategorie.value'), tag: 'Agreement_Product_Category_Value' },
			{
				name: t('tag.agreement.productcategorie.value.vat25'),
				tag: 'Agreement_Product_Category_Value_VAT25'
			}
		],
		order: [
			{ name: t('tag.order.idDescription'), tag: 'Order_ID' },
			{ name: t('tag.orderDescription'), tag: 'Order_Description' },
			{ name: t('tag.order.notes'), tag: 'Order_Notes' },
			{ name: t('tag.order.date'), tag: 'Order_Date' },
			{ name: t('tag.order.closedate'), tag: 'Order_Close_Date' },
			{ name: t('tag.order.ordervalue'), tag: 'Order_Total' },
			{ name: t('tag.order.ordervalue.nc'), tag: 'Order_Total_NC', pdfTemplateTag: 'order.orderValue' },
			{
				name: t('tag.order.ordervalue.vat25'),
				tag: 'Order_Total_VAT25',
				pdfTemplateTag: 'order.orderValue_vat25',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '1.25',
				operator: '*',
				useFormatter: true
			},
			{
				name: t('tag.order.ordervalue.vat25.nc'),
				tag: 'Order_Total_VAT25_NC',
				pdfTemplateTag: 'order.orderValue_vat25_nc',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '1.25',
				operator: '*'
			},
			{
				name: t('tag.order.ordervalue.vat12'),
				tag: 'Order_Total_VAT12',
				pdfTemplateTag: 'order.orderValue_vat12',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '1.12',
				operator: '*',
				useFormatter: true
			},
			{
				name: t('tag.order.ordervalue.vat12.nc'),
				tag: 'Order_Total_VAT12_NC',
				pdfTemplateTag: 'order.orderValue_vat12_nc',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '1.12',
				operator: '*'
			},
			{
				name: t('tag.order.ordervalue.vat6'),
				tag: 'Order_Total_VAT6',
				pdfTemplateTag: 'order.orderValue_vat6',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '1.06',
				operator: '*',
				useFormatter: true
			},
			{
				name: t('tag.order.ordervalue.vat6.nc'),
				tag: 'Order_Total_VAT6_NC',
				pdfTemplateTag: 'order.orderValue_vat6_nc',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '1.06',
				operator: '*'
			},
			{
				name: t('tag.order.purchaseCost'),
				tag: 'Order_Purchase_Cost',
				pdfTemplateTag: 'order.purchasecost',
				value: 'order.purchasecost',
				useFormatter: true
			}, //Total order purchase cost
			{
				name: t('tag.order.purchaseCost.nc'),
				tag: 'Order_Purchase_Cost_NC',
				pdfTemplateTag: 'order.purchasecost'
			}, //Total order purchase cost, no currency
			{
				name: t('tag.order.contributionMargin'),
				tag: 'Order_Contribution_Margin',
				pdfTemplateTag: 'order.contributionMargin',
				value: 'order.contributionMargin',
				useFormatter: true
			}, // Total order contribution margin
			{
				name: t('tag.order.contributionMargin.nc'),
				tag: 'Order_Contribution_Margin_NC',
				pdfTemplateTag: 'order.contributionMarginLocalCurrency'
			}, // Total order contribution margin, no currency
			{
				name: t('tag.order.vat25'),
				tag: 'Order_VAT25',
				pdfTemplateTag: 'order.vat25',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '0.25',
				operator: '*',
				useFormatter: true
			},
			{
				name: t('tag.order.vat25.nc'),
				tag: 'Order_VAT25_NC',
				pdfTemplateTag: 'order.vat25_nc',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '0.25',
				operator: '*'
			},
			{
				name: t('tag.order.vat12'),
				tag: 'Order_VAT12',
				pdfTemplateTag: 'order.vat12',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '0.12',
				operator: '*',
				useFormatter: true
			},
			{
				name: t('tag.order.vat12.nc'),
				tag: 'Order_VAT12_NC',
				pdfTemplateTag: 'order.vat12_nc',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '0.12',
				operator: '*'
			},
			{
				name: t('tag.order.vat6'),
				tag: 'Order_VAT6',
				pdfTemplateTag: 'order.vat6',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '0.06',
				operator: '*',
				useFormatter: true
			},
			{
				name: t('tag.order.vat6.nc'),
				tag: 'Order_VAT6_NC',
				pdfTemplateTag: 'order.vat6_nc',
				useMath: true,
				valueOne: 'order.orderValue',
				valueTwo: '0.06',
				operator: '*'
			},
			{ name: t('tag.order.osc.id'), tag: 'OCR_ID', pdfTemplateTag: 'order.clientConnection.id' },
			{ name: t('tag.order.osc.name'), tag: 'OCR_Name', pdfTemplateTag: 'order.clientConnection.name' },
			{
				name: t('tag.order.osc.address'),
				tag: 'OCR_Address',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Mail.address'
			},
			{
				name: t('tag.order.osc.zipcode'),
				tag: 'OCR_Zipcode',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Mail.zipcode'
			},
			{
				name: t('tag.order.osc.state'),
				tag: 'OCR_State',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Mail.state'
			},
			{
				name: t('tag.order.osc.phone'),
				tag: 'OCR_Phone',
				pdfTemplateTag: 'order.clientConnection.phone'
			},
			{ name: t('tag.order.osc.fax'), tag: 'OCR_Fax', pdfTemplateTag: 'order.clientConnection.fax' },
			{ name: t('tag.order.osc.web'), tag: 'OCR_Web', pdfTemplateTag: 'order.clientConnection.webpage' },
			{
				name: t('tag.order.osc.notes'),
				tag: 'OCR_Notes',
				pdfTemplateTag: 'order.clientConnection.notes'
			},
			{ name: t('tag.order.osc.user'), tag: 'OCR_User' },
			{
				name: t('tag.order.osc.visiting.address'),
				tag: 'OCR_Address_Visiting',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Visit.address'
			},
			{
				name: t('tag.order.osc.visiting.zipcode'),
				tag: 'OCR_Zipcode_Visiting',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Visit.zipcode'
			},
			{
				name: t('tag.order.osc.visiting.city'),
				tag: 'OCR_City_Visiting',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Visit.city'
			},
			{
				name: t('tag.order.osc.visiting.state'),
				tag: 'OCR_State_Visiting',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Visit.state'
			},
			{
				name: t('tag.order.osc.visiting.country'),
				tag: 'OCR_Country_Visiting',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Visit.country'
			},
			{
				name: t('tag.order.osc.invoice.address'),
				tag: 'OCR_Address_Invoice',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Invoice.address'
			},
			{
				name: t('tag.order.osc.invoice.zipcode'),
				tag: 'OCR_Zipcode_Invoice',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Invoice.zipcode'
			},
			{
				name: t('tag.order.osc.invoice.city'),
				tag: 'OCR_City_Invoice',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Invoice.city'
			},
			{
				name: t('tag.order.osc.invoice.state'),
				tag: 'OCR_State_Invoice',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Invoice.state'
			},
			{
				name: t('tag.order.osc.invoice.country'),
				tag: 'OCR_Country_Invoice',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Invoice.country'
			},
			{
				name: t('tag.order.osc.delivery.address'),
				tag: 'OCR_Address_Delivery',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Delivery.address'
			},
			{
				name: t('tag.order.osc.delivery.zipcode'),
				tag: 'OCR_Zipcode_Delivery',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Delivery.zipcode'
			},
			{
				name: t('tag.order.osc.delivery.city'),
				tag: 'OCR_City_Delivery',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Delivery.city'
			},
			{
				name: t('tag.order.osc.delivery.state'),
				tag: 'OCR_State_Delivery',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Delivery.state'
			},
			{
				name: t('tag.order.osc.delivery.country'),
				tag: 'OCR_Country_Delivery',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Delivery.country'
			},
			{
				name: t('tag.order.osc.other.address'),
				tag: 'OCR_Address_Other',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Other.address'
			},
			{
				name: t('tag.order.osc.other.zipcode'),
				tag: 'OCR_Zipcode_Other',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Other.zipcode'
			},
			{
				name: t('tag.order.osc.other.city'),
				tag: 'OCR_City_Other',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Other.city'
			},
			{
				name: t('tag.order.osc.other.state'),
				tag: 'OCR_State_Other',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Other.state'
			},
			{
				name: t('tag.order.osc.other.country'),
				tag: 'OCR_Country_Other',
				pdfTemplateTag: 'order.clientConnection.tagAddresses.Other.country'
			},
			{
				name: t('tag.order.products.tablestart'),
				tag: 'TableStart_Products',
				pdfTemplateTag: 'order.productTablestart',
				value: '#each order.orderRow',
				rowProperty: true
			},
			{
				name: t('tag.order.products.tableend'),
				tag: 'TableEnd_Products',
				pdfTemplateTag: 'order.productTableend',
				value: '/each',
				rowProperty: true
			},
			{ name: t('tag.order.user.name'), tag: 'Order_User', pdfTemplateTag: 'order.user.name' },
			{ name: t('tag.order.user.email'), tag: 'Order_User_Email', pdfTemplateTag: 'order.user.email' },
			{
				name: t('tag.order.user.phone'),
				tag: 'Order_User_Phone',
				pdfTemplateTag: 'order.user.userPhone'
			},
			{
				name: t('tag.order.user.cellphone'),
				tag: 'Order_User_CellPhone',
				pdfTemplateTag: 'order.user.userCellPhone'
			},
			{
				name: t('tag.order.user.title'),
				tag: 'Order_User_Title',
				pdfTemplateTag: 'order.user.userTitle'
			},
			{
				name: t('tag.order.user.address'),
				tag: 'Order_User_Address',
				pdfTemplateTag: 'order.user.userAddress'
			},
			{
				name: t('tag.order.user.zipcode'),
				tag: 'Order_User_Zipcode',
				pdfTemplateTag: 'order.user.userZipCode'
			},
			{
				name: t('tag.order.user.state'),
				tag: 'Order_User_State',
				pdfTemplateTag: 'order.user.userState'
			},
			{
				name: t('tag.order.product.name'),
				tag: 'Prod_Name',
				pdfTemplateTag: 'order.productName',
				value: 'product.name',
				orderRow: true
			},
			{
				name: t('product.articleNo'),
				tag: 'Prod_Article_No',
				pdfTemplateTag: 'order.product.articleNo',
				value: 'product.articleNo',
				orderRow: true
			},
			{
				name: t('tag.order.product.quantity'),
				tag: 'Prod_Quantity',
				pdfTemplateTag: 'order.productQuantity',
				value: 'quantity',
				orderRow: true
			},
			{ name: t('tag.order.product.quantity.decimals'), tag: 'Prod_Decimals_Quantity' },
			{
				name: t('tag.order.product.price'),
				tag: 'Prod_Price',
				pdfTemplateTag: 'order.productPrice',
				value: 'price',
				orderRow: true,
				useFormatter: true
			},
			{
				name: t('tag.order.product.price.nc'),
				tag: 'Prod_Price_NC',
				pdfTemplateTag: 'order.productPriceNc',
				value: 'price',
				orderRow: true
			},
			{
				name: t('tag.order.product.sum'),
				tag: 'Prod_Sum',
				pdfTemplateTag: 'order.productSum',
				orderRow: true,
				useMath: true,
				valueOne: 'price',
				valueTwo: 'quantity',
				operator: '*',
				useFormatter: true
			},
			{
				name: t('tag.order.product.sum.nc'),
				tag: 'Prod_Sum_NC',
				pdfTemplateTag: 'order.productSum_nc',
				orderRow: true,
				useMath: true,
				valueOne: 'price',
				valueTwo: 'quantity',
				operator: '*'
			},
			{ name: t('tag.order.product.category.name'), tag: 'Prod_Cat_Name' },

			{ name: t('tag.order.discount'), tag: 'Total_Discount' },
			{ name: t('tag.order.discount.nc'), tag: 'Total_Discount_NC' },
			{
				name: t('tag.order.product.discount'),
				tag: 'Product_Discount',
				pdfTemplateTag: 'order.productDiscount',
				value: 'discount',
				orderRow: true,
				useMath: true,
				valueOne: 'listPrice',
				valueTwo: 'price',
				operator: '-',
				useFormatter: true
			},
			{
				name: t('tag.order.product.discount.nc'),
				tag: 'Product_Discount_NC',
				pdfTemplateTag: 'order.productDiscount_nc',
				value: 'discount',
				orderRow: true,
				useMath: true,
				valueOne: 'listPrice',
				valueTwo: 'price',
				operator: '-'
			},
			{ name: t('tag.order.product.listprice.sum'), tag: 'Product_ListPrice_Sum' },
			{ name: t('tag.order.product.listprice.sum.nc'), tag: 'Product_ListPrice_Sum_NC' },

			{ name: t('tag.order.listprice.sum'), tag: 'Order_ListPrice' },
			{ name: t('tag.order.listprice.sum.nc'), tag: 'Order_ListPrice_NC' },
			{
				name: t('tag.order.product.discount.percentage'),
				tag: 'Product_Discount_Percentage',
				pdfTemplateTag: 'order.productDiscountPercentage',
				orderRow: true,
				valueOne: 'listPrice',
				valueTwo: 'price',
				operator: '-',
				usePercentDiscount: true
			},
			{ name: t('tag.order.discount.percentage'), tag: 'Total_Discount_Percentage' },
			{ name: t('tag.order.quantity'), tag: 'Order_Total_Quantity' },
			{
				name: t('tag.order.orderRow.listprice'),
				tag: 'Prod_ListPrice',
				pdfTemplateTag: 'order.productListprice',
				value: 'listPrice',
				orderRow: true,
				useFormatter: true
			},
			{
				name: t('tag.order.orderRow.listprice.nc'),
				tag: 'Prod_ListPrice_NC',
				pdfTemplateTag: 'order.productListprice_nc',
				value: 'listPrice',
				orderRow: true
			},
			{
				name: t('tag.order.product.vat25'),
				tag: 'Prod_Sum_VAT25',
				pdfTemplateTag: 'order.productSumvat25',
				orderRow: true,
				useMath: true,
				valueOne: 'price',
				valueTwo: 'quantity',
				operator: '*',
				valueThree: '1.25',
				secondOperator: '*',
				useFormatter: true
			},
			{
				name: t('tag.order.product.vat25.nc'),
				tag: 'Prod_Sum_VAT25_NC',
				pdfTemplateTag: 'order.productSumvat25_nc',
				orderRow: true,
				useMath: true,
				valueOne: 'price',
				valueTwo: 'quantity',
				operator: '*',
				valueThree: '1.25',
				secondOperator: '*'
			},
			{
				name: t('tag.order.product.purchaseCost'),
				tag: 'Prod_Purchase_Cost',
				pdfTemplateTag: 'order.productPurchaseCost',
				value: 'purchaseCost',
				orderRow: true,
				useFormatter: true
			},
			{
				name: t('tag.order.product.purchaseCost.nc'),
				tag: 'Prod_Purchase_Cost_NC',
				pdfTemplateTag: 'order.productPurchaseCost_nc',
				value: 'purchaseCost',
				orderRow: true
			},
			{ name: t('tag.order.product.purchaseCost.total'), tag: 'Prod_Purchase_Cost_Sum' },
			{ name: t('tag.order.product.purchaseCost.total.nc'), tag: 'Prod_Purchase_Cost_Sum_NC' },
			{
				name: t('tag.order.product.contributionMargin'),
				tag: 'Prod_Contribution_Margin',
				pdfTemplateTag: 'order.contributionMargin',
				value: 'purchaseCost',
				orderRow: true,
				useMath: true,
				valueOne: 'price',
				valueTwo: 'purchaseCost',
				operator: '-',
				useFormatter: true
			},
			{
				name: t('tag.order.product.contributionMargin.nc'),
				tag: 'Prod_Contribution_Margin_NC',
				pdfTemplateTag: 'order.contributionMargin_nc',
				value: 'purchaseCost',
				orderRow: true,
				useMath: true,
				valueOne: 'price',
				valueTwo: 'purchaseCost',
				operator: '-'
			},
			{ name: t('tag.order.product.contributionMargin.total'), tag: 'Prod_Contribution_Margin_Sum' },
			{
				name: t('tag.order.product.contributionMargin.total.nc'),
				tag: 'Prod_Contribution_Margin_Sum_NC'
			},
			{
				name: t('tag.order.project.name'),
				tag: 'Project_Name',
				pdfTemplateTag: 'order.projectName',
				value: 'order.project.name',
				rowProperty: true
			},
			{
				name: t('tag.order.project.quota'),
				tag: 'Project_Quota',
				pdfTemplateTag: 'order.projectQuota',
				value: 'order.project.quota',
				rowProperty: true
			},
			{
				name: t('tag.order.project.quota.nc'),
				tag: 'Project_Quota_NC',
				pdfTemplateTag: 'order.projectQuota_nc',
				value: 'order.project.quota',
				useFormatter: true
			},
			{
				name: t('tag.order.project.startdate'),
				tag: 'Project_StartDate',
				pdfTemplateTag: 'order.projectStartdate',
				value: 'order.project.startDate',
				rowProperty: true
			},
			{
				name: t('tag.order.project.endate'),
				tag: 'Project_EndDate',
				pdfTemplateTag: 'order.projectEndate',
				value: 'order.project.endDate',
				rowProperty: true
			},
			{
				name: t('tag.order.project.notes'),
				tag: 'Project_Notes',
				pdfTemplateTag: 'order.projectNotes',
				value: 'order.project.notes',
				rowProperty: true
			},
			{ name: t('tag.order.listprice'), tag: 'Total_ListPrice_Sum' },
			{ name: t('tag.order.listprice.nc'), tag: 'Total_ListPrice_Sum_NC' }
		],
		project: [
			{ name: t('tag.project.projectId'), tag: 'Project_ID' },
			{ name: t('tag.project.projectName'), tag: 'Project_Name' },
			{ name: t('tag.project.projectNotes'), tag: 'Project_Notes' },
			{ name: t('tag.project.projectStartdate'), tag: 'Project_Startdate' },
			{ name: t('tag.project.projectEnddate'), tag: 'Project_Enddate' },
			{ name: t('tag.project.projectQuota'), tag: 'Project_Quota' },
			{ name: t('tag.project.projectQuotaNc'), tag: 'Project_Quota_NC' }
		]
	};

	[...AppService.getProductCategories()]
		.sort((a: { id: number }, b: { id: number }) => a.id - b.id)
		.forEach((cat: { id: number; name: string }) => {
			tags.order.push({
				name: t('tag.order.products.tablestart.category') + ' ' + cat.name,
				tag: 'TableStart_Products_' + cat.id
			});
			tags.order.push({
				name: t('tag.order.products.tableend.category') + ' ' + cat.name,
				tag: 'TableEnd_Products_' + cat.id
			});
		});

	cached = tags;
	return tags;
};

const getClientTags = (): ClassicTag[] => {
	const tags = clone(buildTags().client);
	const cfTags = generateCustomFieldTags('Cl', 'account');
	const cfCoTags = generateCustomFieldTags('Co', 'contact');
	const clientNewTags = hasSoftDeployAccess(Feature.NEW_FIELDS) ? clone(buildTags().clientNew) : ([] as ClassicTag[]);

	return tags.concat(clientNewTags).concat(cfTags).concat(cfCoTags);
};

const getOrderTags = (): ClassicTag[] => {
	const orderTags = clone(buildTags().order).filter(tag => !FILTERED_TAGS.includes(tag.tag));

	return orderTags
		.concat(generateCustomFieldTags('Order', 'order'))
		.concat(getClientTags())
		.concat(generateCustomFieldTags('Project', 'project'))
		.concat(generateCustomFieldTags('Product', 'product'))
		.concat(generateCustomFieldTags('OCR', 'account'))
		.concat(generateCustomFieldTags('Order_Row', 'orderrow'));
};

const getAgreementTags = (): ClassicTag[] =>
	clone(buildTags().agreement).concat(generateCustomFieldTags('Agreement', 'agreement')).concat(getClientTags());

const getActivityTags = (): ClassicTag[] =>
	clone(buildTags().activity)
		.concat(generateCustomFieldTags('Act', 'activity'))
		.concat(generateCustomFieldTags('Act', 'appointment'))
		.concat(getClientTags())
		// the trailing space in ' project' is in the Angular original; getCustomFields returns
		// nothing for it, so the call contributes no tags. Kept so the output is identical.
		.concat(generateCustomFieldTags('Project', ' project' as CustomFieldKey));

const getUdoTags = (id: number): ClassicTag[] => {
	const prefix = `U${id}`;
	const udos = AppService.getMetadata().params.UserDefinedObject;
	const udo = (udos ?? []).find((u: { id: number }) => u.id === id) as { name: string; linkType: string };

	let res: ClassicTag[] = [{ name: `${udo.name} ${t('default.id2').toLowerCase()}`, tag: `${prefix}_ID` }];

	if (udo.linkType === 'contact' || udo.linkType === 'client') {
		res = res.concat(getClientTags());
	} else if (udo.linkType === 'project') {
		res = res.concat(generateCustomFieldTags('Project', 'project')).concat(clone(buildTags().project));
	}

	return res.concat(generateCustomFieldTags(prefix, `userDefined${id}` as CustomFieldKey));
};

/*
	The only member consumed outside the Angular tree. Returns undefined for an unknown entity, as
	the Angular switch did — callers pass entity names built at runtime (a udo id, a modal param),
	so the undefined is part of the contract rather than an impossible case.
*/
export const getClassicTagsByEntity = (entity: string): ClassicTag[] | undefined => {
	switch (entity) {
		case 'Client':
			return getClientTags();
		case 'Order':
		case 'order':
			return getOrderTags();
		case 'Agreement':
			return getAgreementTags();
		case 'Activity':
			return getActivityTags();
		case 'UserDefinedObject1':
			return getUdoTags(1);
		case 'UserDefinedObject2':
			return getUdoTags(2);
		case 'UserDefinedObject3':
			return getUdoTags(3);
		case 'UserDefinedObject4':
			return getUdoTags(4);
		default:
			return undefined;
	}
};
